/*
 * The legacy CareIQ tab's modals, COPIED unchanged from CareIQTab.jsx (which
 * N3 leaves untouched, so they cannot be imported from there): the visit
 * summary (BlinkScribe note / encounter summary / handwritten note / order
 * history), the lab mini-table and the generic detail modal. They keep the
 * .ciqt-modal* classes of CareIQTab.css. The copies go away with the legacy
 * tab (BUILD-PLAN Step 7).
 * One N3 change (source links, careiq.n3.2): the lab mini-table can be
 * opened FOR a record (a recommendation's evidence, a Follow up lab row):
 * labGroup.focus names its tests, whose panel comes first and whose rows are
 * marked; labGroup.partial says the popup was built from the record's key
 * values alone. Esc closes it.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import Editscreen from "../../../../../blinkscribe/archeivescreens/editscreen";
import { testMatchesFocus } from "./sourceLinks";
import careiqapi from "../../../../../api/careiqapi";
import blinkscribeapi from "../../../../../api/blinkscribeapi";
import ClinicalEncounterDashboard from "../../priorvisits/ClinicalEncounterDashboard";
import { getPracticeId } from "../../../../../config/practice";
import { fetchRemainingPages } from "../documentPages";
import "../CareIQTab.css";

const practiceId = getPracticeId();

/* ═══ Visit Summary Modal (Tabbed: BlinkScribe + Encounter Summary) ═══ */
export const VisitSummaryModal = ({ encounter, patientId, patientInfo, onClose }) => {
    // BS-only synthetic visits (no Athena encounter) open on the tab that
    // actually has content.
    // BlinkScribe (the Edit interface) is the default tab for every visit
    // opened from the timeline (user request 2026-09-01).
    const [activeTab, setActiveTab] = useState("blinkscribe");
    const [bsLoading, setBsLoading] = useState(false);
    const [bsRecord, setBsRecord] = useState(null);
    const [bsError, setBsError] = useState(false);
    // Full note content — recording/details returns METADATA ONLY (no
    // subjective/hpi/assessment fields), so the tab rendered "Note content
    // not yet available" for every visit until this second fetch was added
    // (QA 2026-08-19, pt 13795).
    const [bsNote, setBsNote] = useState(null);
    const [esLoading, setEsLoading] = useState(false);
    const [esData, setEsData] = useState(null);
    const [esError, setEsError] = useState(false);
    // Handwritten Note state
    const [hwLoading, setHwLoading] = useState(false);
    const [hwPages, setHwPages] = useState(null); // null = not fetched, [] = empty
    const [hwError, setHwError] = useState(false);
    const [hwDocDesc, setHwDocDesc] = useState("");
    const [hwMatch, setHwMatch] = useState(undefined); // undefined = checking, null = no match, object = match found
    // Order History state
    const [ohLoading, setOhLoading] = useState(false);
    const [ohOrders, setOhOrders] = useState(null); // null = not fetched
    const [ohError, setOhError] = useState(false);
    const [ohClaims, setOhClaims] = useState(null);
    const userConfig = useSelector((state) => state.userconfig);
    const deptId = userConfig?.config?.departmentid || 1;

    const visitDate = encounter.startdate || encounter.date || encounter.encounterdate;
    const encounterId = encounter.encounterid;

    /* Fetch BlinkScribe note for the visit date */
    useEffect(() => {
        if (!patientId || !visitDate) return;
        let cancelled = false;
        const fetchBS = async () => {
            setBsLoading(true);
            setBsError(false);
            setBsNote(null);
            try {
                const dateStr = dayjs(visitDate).format("YYYY-MM-DD");
                const res = await blinkscribeapi.post(
                    `/v1/patient/${patientId}/recording/details`,
                    { filter_array: { date_filter: { start_date: dateStr, end_date: dateStr } } }
                );
                const data = res?.data || res;
                const rec = Array.isArray(data) && data.length > 0 ? data[0] : null;
                if (!cancelled) setBsRecord(rec);
                if (rec?.id) {
                    try {
                        const noteRes = await blinkscribeapi.get(`/v1/recording/note/${rec.id}/get`);
                        const row = (noteRes?.data && noteRes.data[0]) || (Array.isArray(noteRes) ? noteRes[0] : null);
                        let nj = row?.note_json;
                        if (typeof nj === "string") { try { nj = JSON.parse(nj); } catch { nj = null; } }
                        if (nj && nj.soap_note && typeof nj.soap_note === "object") nj = { ...nj.soap_note, ...nj };
                        if (!cancelled) setBsNote(nj && typeof nj === "object" ? nj : null);
                    } catch { /* fall back to the metadata-only view */ }
                }
            } catch {
                if (!cancelled) setBsError(true);
            } finally {
                if (!cancelled) setBsLoading(false);
            }
        };
        fetchBS();
        return () => { cancelled = true; };
    }, [patientId, visitDate]);

    /* Fetch Encounter Summary (structured data) */
    useEffect(() => {
        if (!encounterId) return;
        if (esData) return; // already fetched
        let cancelled = false;
        const fetchES = async () => {
            setEsLoading(true);
            setEsError(false);
            try {
                const res = await careiqapi.post(
                    `/v1/fhir-mirror/${practiceId}/encounter/specific-summary`,
                    { encounter_id: encounterId, patient_id: parseInt(patientId) || 0 }
                );
                if (!cancelled) {
                    const raw = res?.data?.data || res?.data || res;
                    setEsData(raw);
                }
            } catch {
                if (!cancelled) setEsError(true);
            } finally {
                if (!cancelled) setEsLoading(false);
            }
        };
        fetchES();
        return () => { cancelled = true; };
    }, [encounterId, activeTab, esData]);

    /* Pre-check: fetch progress notes list on mount to know if handwritten note exists */
    useEffect(() => {
        if (!patientId || !visitDate) return;
        let cancelled = false;
        const checkHW = async () => {
            try {
                const visitDayjs = dayjs(visitDate);
                const listRes = await careiqapi.post(
                    `/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/get`,
                    { category: "ProgressNotes", department_id: deptId }
                );
                const resData = listRes?.data || listRes;
                const docs = resData?.encounterdocuments || resData?.data || [];
                // Filter out non-handwritten documents (EKGs, imaging, etc.)
                const isHandwrittenNote = (d) => {
                    const desc = (d.documentdescription || d.description || "").toLowerCase();
                    const excludePatterns = ["ekg", "ecg", "electrocardiogram", "imaging", "x-ray", "xray", "mri", "ct scan", "ultrasound", "lab"];
                    return !excludePatterns.some(p => desc.includes(p));
                };
                const filteredDocs = docs.filter(isHandwrittenNote);

                // Match by encounter ID first, then fall back to date
                let match = filteredDocs.find(d => {
                    const docEncId = String(d.encounterid || "");
                    const targetId = String(encounterId || "");
                    return docEncId && targetId && docEncId === targetId;
                });
                if (!match) {
                    const candidates = filteredDocs
                        .map(d => ({ ...d, _dt: dayjs(d.createddatetime || d.createddate) }))
                        .filter(d => {
                            const diff = d._dt.diff(visitDayjs, 'day');
                            return diff >= 0 && diff <= 7;
                        })
                        .sort((a, b) => a._dt.valueOf() - b._dt.valueOf());
                    match = candidates[0] || null;
                }
                if (!cancelled) setHwMatch(match || null);
            } catch {
                if (!cancelled) setHwMatch(null);
            }
        };
        checkHW();
        return () => { cancelled = true; };
    }, [patientId, visitDate]);

    /* Fetch Handwritten (Progress) Note content when tab is opened */
    useEffect(() => {
        if (activeTab !== "handwritten" || !patientId || !visitDate) return;
        if (hwPages !== null) return; // already fetched
        if (!hwMatch) return; // no matching doc
        let cancelled = false;
        const fetchHW = async () => {
            setHwLoading(true);
            setHwError(false);
            try {
                if (!cancelled) setHwDocDesc(hwMatch.documentdescription || "Progress Note");
                const docId = hwMatch.encounterdocumentid;
                const hwUrl = `/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/image-result`;
                const hwBody = {
                    patientid: patientId,
                    imagingresultid: docId,
                    fhir_resource_id: hwMatch.fhir_resource_id || null,
                    category: "ProgressNotes",
                };
                const contentRes = await careiqapi.post(hwUrl, { ...hwBody, page_offset: 0, page_limit: 20 });
                const contentData = contentRes?.data || contentRes;
                const firstEntry = Array.isArray(contentData) ? contentData[0] : contentData;
                const firstPages = firstEntry?.pages || [];
                // A note longer than the first 20 pages: the rest too (owner 10-07, "I can
                // only see 5"; review F7). Free: v2 cache / FHIR. No extra request otherwise.
                const pages = (Number(firstEntry?.total_pages) || 0) > firstPages.length
                    ? await fetchRemainingPages((u, b) => careiqapi.post(u, b), hwUrl, hwBody,
                        firstPages, firstEntry.total_pages, { isCancelled: () => cancelled })
                    : firstPages;
                if (!cancelled) setHwPages(pages);
            } catch (err) {
                console.error("[VisitModal] Handwritten note fetch error:", err);
                if (!cancelled) setHwError(true);
            } finally {
                if (!cancelled) setHwLoading(false);
            }
        };
        fetchHW();
        return () => { cancelled = true; };
    }, [activeTab, patientId, visitDate, hwPages, hwMatch]);


    /* Fetch Order History for the visit date */
    useEffect(() => {
        if (activeTab !== "orders" || !patientId || !visitDate) return;
        if (ohOrders !== null) return; // already fetched
        let cancelled = false;
        const fetchOH = async () => {
            setOhLoading(true);
            setOhError(false);
            try {
                const dateStr = dayjs(visitDate).format("YYYY-MM-DD");
                const res = await careiqapi.post(`/v1/${practiceId}/orders/get`, {
                    patientid: patientId,
                    orderdate: dateStr,
                });
                const d = res?.data || res;
                const orders = Array.isArray(d) ? d : (d?.data || d?.orders || []);
                if (!cancelled) { setOhOrders(orders); setOhLoading(false); }
                // Fire-and-forget claims fetch (simple DB read, no Athena call)
                careiqapi.get(`/v1/${practiceId}/claims/by-date?patientid=${patientId}&service_date=${dateStr}`)
                    .then(claimsRes => {
                        const cd = claimsRes?.data || claimsRes;
                        if (!cancelled) setOhClaims(cd?.data || []);
                    })
                    .catch(e => console.warn("[VisitModal] Claims:", e?.message));
            } catch (err) {
                console.error("[VisitModal] Order history fetch error:", err);
                if (!cancelled) { setOhError(true); setOhLoading(false); }
            }
        };
        fetchOH();
        return () => { cancelled = true; };
    }, [activeTab, patientId, visitDate, ohOrders]);

    const fields = [
        ["Date", visitDate],
        ["Type", encounter.appointmenttypename],
        ["Provider", encounter.providerfullname || encounter.providerid],
        ["Status", encounter.appointmentstatus || encounter.status],
    ];

    // Who actually produced the note (user request 2026-09-02): the
    // sign-off name wins, then the recorder, then the appointment's
    // scheduled provider. bsRecord loads async, so the chip appears as
    // soon as the recording fetch lands.
    const noteSigner = bsRecord?.signed_by_name || "";
    const noteRecorder = bsRecord?.recorded_by || "";
    const noteProvider = noteSigner || noteRecorder ||
        bsRecord?.providername || encounter.providerfullname || "";
    const noteProviderRole = noteSigner ? "signed off" : noteRecorder ? "recorded" : "";

    return (
        <div className="ciqt-modal-bg" onClick={onClose}>
            <div className="ciqt-modal ciqt-modal-lg" onClick={(e) => e.stopPropagation()}>
                {/* Header with visit info */}
                <div className="ciqt-modal-head">
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span className="ciqt-modal-t">{encounter.appointmenttypename || "Visit Details"}</span>
                            {noteProvider && (
                                <span style={{
                                    background: "#EEF2FF", color: "#3730A3", fontWeight: 700,
                                    fontSize: 13, padding: "3px 12px", borderRadius: 999,
                                    whiteSpace: "nowrap", display: "inline-flex",
                                    alignItems: "center", gap: 6,
                                }}>
                                    🩺 {noteProvider}
                                    {noteProviderRole && (
                                        <span style={{ fontWeight: 500, color: "#6366F1", fontSize: 11 }}>
                                            {noteProviderRole}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                            {visitDate ? dayjs(visitDate).format("MMMM D, YYYY") : ""}
                            {/* Recorder shown alongside when someone else signed off. */}
                            {noteSigner && noteRecorder && noteRecorder !== noteSigner
                                ? ` · Recorded by ${noteRecorder}` : ""}
                            {encounter.providerfullname && encounter.providerfullname !== noteProvider
                                ? ` · ${encounter.providerfullname}` : ""}
                        </div>
                    </div>
                    <button className="ciqt-modal-x" onClick={onClose}>✕</button>
                </div>

                {/* Tabs */}
                <div className="ciqt-visit-tabs">
                    <button
                        className={`ciqt-visit-tab ${activeTab === "blinkscribe" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("blinkscribe")}
                    >
                        📝 BlinkScribe Note
                    </button>
                    <button
                        className={`ciqt-visit-tab ${activeTab === "summary" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("summary")}
                    >
                        📋 Encounter Summary
                    </button>
                    {hwMatch !== null && (
                    <button
                        className={`ciqt-visit-tab ${activeTab === "handwritten" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("handwritten")}
                    >
                        ✍️ Handwritten Note
                    </button>
                    )}
                    <button
                        className={`ciqt-visit-tab ${activeTab === "orders" ? "ciqt-visit-tab-active" : ""}`}
                        onClick={() => setActiveTab("orders")}
                    >
                        📦 Order History
                    </button>
                </div>

                {/* Tab Content */}
                <div className="ciqt-modal-body" style={{ minHeight: 200 }}>
                    {activeTab === "blinkscribe" && (
                        <div>
                            {bsLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading BlinkScribe note…</span>
                                </div>
                            ) : bsError ? (
                                <div className="ciqt-empty">Failed to load BlinkScribe note</div>
                            ) : bsRecord?.id ? (
                                /* The full Edit interface — the same one as the
                                   archive's Edit modal, rendered INLINE in this
                                   tab (embedded — no portaled overlay stacking a
                                   second dialog on top of the visit modal; user
                                   request 2026-09-02). Sign-off closes the visit
                                   modal entirely. */
                                <Editscreen
                                    selectedRecord={bsRecord}
                                    embedded
                                    setIsEditScreen={(v) => { if (!v) setActiveTab("summary"); }}
                                    onDone={onClose}
                                />
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">📝</div>
                                    <div className="ciqt-empty-title">No BlinkScribe Note</div>
                                    <div className="ciqt-empty-desc">No recording found for this visit date</div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "summary" && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                            {esLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading encounter summary…</span>
                                </div>
                            ) : esError ? (
                                <div className="ciqt-empty">Failed to load encounter summary</div>
                            ) : esData ? (
                                <>
                                    {hwMatch && (
                                        <div style={{background:'#FFFBEB',border:'1px solid #FCD34D',color:'#92400E',
                                                     fontSize:'13px',padding:'6px 12px',borderRadius:'6px',margin:'6px 0'}}>
                                            This visit was documented as a handwritten note - structured sections
                                            (Chief Complaint, HPI, Assessment/Plan) may be blank here. Open the
                                            Handwritten Note tab for the full documentation.
                                        </div>
                                    )}
                                    <ClinicalEncounterDashboard rawData={esData} fallbackPatient={patientInfo} />
                                </>
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">📋</div>
                                    <div className="ciqt-empty-title">No Encounter Summary</div>
                                    <div className="ciqt-empty-desc">Summary not available for this encounter</div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "handwritten" && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                            {hwLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading handwritten note…</span>
                                </div>
                            ) : hwError ? (
                                <div className="ciqt-empty">Failed to load handwritten note</div>
                            ) : hwPages && hwPages.length > 0 ? (
                                <div style={{ padding: "8px 0", display: "flex", flexDirection: "column", height: "100%" }}>
                                    {hwDocDesc && (
                                        <div style={{
                                            fontSize: 12, fontWeight: 600, color: "#64748b",
                                            marginBottom: 8, paddingBottom: 6, width: "100%",
                                            borderBottom: "1px solid #e2e8f0",
                                        }}>
                                            {hwDocDesc} · {hwPages.length} page{hwPages.length !== 1 ? "s" : ""}
                                        </div>
                                    )}
                                    {/* Side-by-side thumbnails that fit the modal */}
                                    <div style={{
                                        display: "flex", gap: 16, justifyContent: "center", alignItems: "center",
                                        flex: 1, minHeight: 0, padding: "8px 0",
                                    }}>
                                        {hwPages.map((page, idx) => {
                                            // only media-type characters: ct ends up inside an HTML attribute (openFull) (review 10-07n finding 3)
                                            const ct = String(page.content_type || "image/png").toLowerCase().replace(/[^a-z0-9.+/;=-]/g, "") || "image/png";
                                            const isPdf = ct.includes("pdf");
                                            const src = page.data ? (isPdf ? null : `data:${ct};base64,${page.data}`) : "";
                                            let blobUrl = null;
                                            if (isPdf && page.data) {
                                                try {
                                                    const byteString = atob(page.data);
                                                    const arr = new Uint8Array(byteString.length);
                                                    for (let i = 0; i < byteString.length; i++) arr[i] = byteString.charCodeAt(i);
                                                    // typed application/pdf whatever ct says: a blob: URL runs in the care origin (review 10-07n finding 3)
                                                    const blob = new Blob([arr], { type: "application/pdf" });
                                                    blobUrl = URL.createObjectURL(blob);
                                                } catch (e) { /* ignore */ }
                                            }
                                            // For full-size open
                                            const openFull = () => {
                                                if (blobUrl) { window.open(blobUrl, '_blank'); return; }
                                                if (src) {
                                                    const w = window.open('', '_blank');
                                                    w.document.write(`<html><body style="margin:0;background:#111;display:flex;justify-content:center;align-items:center;min-height:100vh"><img src="${src}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`);
                                                }
                                            };
                                            return (
                                                <div key={idx} style={{
                                                    flex: `0 1 ${hwPages.length === 1 ? '60%' : '45%'}`,
                                                    maxHeight: '100%', display: 'flex', flexDirection: 'column',
                                                    alignItems: 'center', cursor: 'pointer',
                                                }} onClick={openFull} title="Click to view full size">
                                                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>Page {idx + 1}</div>
                                                    {isPdf && blobUrl ? (
                                                        <iframe
                                                            src={blobUrl}
                                                            title={`Page ${idx + 1}`}
                                                            style={{
                                                                width: "100%", flex: 1, maxHeight: "calc(100% - 20px)",
                                                                border: "1px solid #e2e8f0", borderRadius: 8,
                                                                pointerEvents: "none",
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={src}
                                                            alt={`Page ${idx + 1}`}
                                                            style={{
                                                                maxWidth: "100%", maxHeight: "calc(100% - 20px)",
                                                                objectFit: "contain", borderRadius: 8,
                                                                border: "1px solid #e2e8f0",
                                                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                                                transition: "transform 0.2s, box-shadow 0.2s",
                                                            }}
                                                            onMouseEnter={e => { e.target.style.transform = "scale(1.02)"; e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
                                                            onMouseLeave={e => { e.target.style.transform = "scale(1)"; e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", marginTop: 4 }}>
                                        Click any page to view full size
                                    </div>
                                </div>
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">✍️</div>
                                    <div className="ciqt-empty-title">No Handwritten Note</div>
                                    <div className="ciqt-empty-desc">No progress note found for this visit date</div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "orders" && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                            {ohLoading ? (
                                <div className="ciqt-visit-loading">
                                    <div className="ciqt-spinner" />
                                    <span>Loading order history…</span>
                                </div>
                            ) : ohError ? (
                                <div className="ciqt-empty">Failed to load order history</div>
                            ) : ohOrders && ohOrders.length > 0 ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                                    <div style={{ width: "100%", maxWidth: 550 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>
                                            {ohOrders.length} order{ohOrders.length !== 1 ? "s" : ""} · {visitDate ? dayjs(visitDate).format("MMM D, YYYY") : ""}
                                        </div>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                            <thead>
                                                <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                                                    <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11 }}>Order</th>
                                                    <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 70 }}>Type</th>
                                                    <th style={{ textAlign: "center", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 90 }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ohOrders.map((o, i) => {
                                                    const sl = (o.status || "").toLowerCase();
                                                    const sc = sl.includes("complete") || sl.includes("done") ? { bg: "#DEF7EC", color: "#03543F" }
                                                        : sl.includes("cancel") || sl.includes("reject") ? { bg: "#FDE8E8", color: "#9B1C1C" }
                                                        : sl.includes("pend") || sl.includes("submit") || sl.includes("order") ? { bg: "#FEF3C7", color: "#92400E" }
                                                        : { bg: "#F1F5F9", color: "#475569" };
                                                    return (
                                                        <tr key={o.id || i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fafbfc" : "#fff" }}>
                                                            <td style={{ padding: "6px 8px" }}>
                                                                <div style={{ fontWeight: 600, color: "#1e293b" }}>{o.ordername || "—"}</div>
                                                                {o.diagnosis && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{o.diagnosis}</div>}
                                                            </td>
                                                            <td style={{ padding: "6px 8px", fontSize: 11, color: "#64748b" }}>{o.ordertype || "—"}</td>
                                                            <td style={{ padding: "6px 8px", textAlign: "center" }}>
                                                                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: sc.bg, color: sc.color }}>{o.status || "—"}</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        {/* Claims Data section */}
                                        {ohClaims && ohClaims.length > 0 && (
                                            <div style={{ marginTop: 20 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>
                                                    💰 Billing · {ohClaims.length} line{ohClaims.length !== 1 ? "s" : ""}
                                                </div>
                                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                                                            <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 55 }}>CPT</th>
                                                            <th style={{ textAlign: "left", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11 }}>Description</th>
                                                            <th style={{ textAlign: "center", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 40 }}>Qty</th>
                                                            <th style={{ textAlign: "right", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 70 }}>Charge</th>
                                                            <th style={{ textAlign: "right", padding: "6px 8px", color: "#475569", fontWeight: 700, fontSize: 11, width: 70 }}>Payment</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {ohClaims.map((c, i) => (
                                                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fafbfc" : "#fff" }}>
                                                                <td style={{ padding: "5px 8px", fontWeight: 600, color: "#1e293b", fontFamily: "monospace", fontSize: 11 }}>{c.cpt || "—"}</td>
                                                                <td style={{ padding: "5px 8px", color: "#334155" }}>{c.description || "—"}</td>
                                                                <td style={{ padding: "5px 8px", textAlign: "center", color: "#64748b" }}>{c.units || 1}</td>
                                                                <td style={{ padding: "5px 8px", textAlign: "right", color: "#1e293b" }}>${(c.charge || 0).toFixed(2)}</td>
                                                                <td style={{ padding: "5px 8px", textAlign: "right", color: c.payment > 0 ? "#03543F" : "#64748b" }}>${(c.payment || 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="ciqt-empty-state">
                                    <div className="ciqt-empty-icon">📦</div>
                                    <div className="ciqt-empty-title">No Orders</div>
                                    <div className="ciqt-empty-desc">No orders found for this visit date</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ═══ Labs Mini-Table Modal (grouped by date) ═══ */
export const LabsMiniModal = ({ labGroup, onClose, onSwitchToLabs }) => {
    const closeRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (closeRef.current && closeRef.current.focus) closeRef.current.focus({ preventScroll: true });
        const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); if (onCloseRef.current) onCloseRef.current(); } };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);
    /* Flatten tests + extract values, then group by panel */
    const { panels, abnormalCount, totalCount } = useMemo(() => {
        const panelMap = {};
        const focus = labGroup?.focus || [];
        let abnCount = 0;
        let total = 0;
        (labGroup?.tests || []).forEach(t => {
            const lr = t.latestResult || {};
            const val = lr.value ?? lr.result ?? lr.resultvalue ?? t.value ?? '';
            if (val === '') return;
            const ref = t.refrange || lr.refrange || '';
            const oob = isOutOfRange(val, ref) || Boolean(t.flagged);
            if (oob) abnCount++;
            total++;
            const pn = t.panelname || 'Other';
            const name = t.testname || t.name || '—';
            const focused = testMatchesFocus(name, focus);
            if (!panelMap[pn]) panelMap[pn] = { name: pn, tests: [], hasAbnormal: false, focused: testMatchesFocus(pn, focus) };
            panelMap[pn].tests.push({
                name,
                value: val,
                units: lr.units || lr.unit || '',
                refrange: ref,
                abnormal: oob,
                focused,
            });
            if (oob) panelMap[pn].hasAbnormal = true;
            if (focused) panelMap[pn].focused = true;
        });
        // Sort: the record's own panel first (opened for a record), then panels with abnormals
        const sorted = Object.values(panelMap).sort((a, b) => ((b.focused ? 2 : 0) + (b.hasAbnormal ? 1 : 0)) - ((a.focused ? 2 : 0) + (a.hasAbnormal ? 1 : 0)));
        return { panels: sorted, abnormalCount: abnCount, totalCount: total };
    }, [labGroup]);

    const s = {
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
        modal: { background: '#faf8f5', borderRadius: 16, width: '92%', maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' },
        header: { padding: '20px 24px 16px', borderBottom: '1px solid #e4ded5', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
        title: { fontSize: 18, fontWeight: 700, color: '#1a1613' },
        date: { fontSize: 12, color: '#948b7f', marginTop: 2 },
        stats: { display: 'flex', gap: 8, alignItems: 'center' },
        pill: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#f0ede8', color: '#5c5549' },
        pillAbn: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#FFF0F0', color: '#C53030' },
        closeBtn: { background: 'none', border: 'none', fontSize: 18, color: '#948b7f', cursor: 'pointer', padding: 4 },
        body: { flex: 1, overflow: 'auto', padding: '16px 20px 24px' },
        card: { background: '#fff', border: '1px solid #e4ded5', borderRadius: 12, marginBottom: 12, overflow: 'hidden' },
        cardAbn: { background: '#fff', border: '1px solid #f0bcbc', borderRadius: 12, marginBottom: 12, overflow: 'hidden' },
        cardHead: { padding: '10px 14px', borderBottom: '1px solid #f0ede8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        cardTitle: { fontSize: 13, fontWeight: 700, color: '#1a1613', textTransform: 'capitalize' },
        cardBadge: { fontSize: 9, fontWeight: 700, color: '#C53030', background: '#FFF0F0', padding: '2px 8px', borderRadius: 10 },
        cardBody: { maxHeight: 200, overflow: 'auto' },
        row: { display: 'flex', alignItems: 'center', padding: '6px 14px', borderBottom: '1px solid #f5f3ef' },
        rowAbn: { display: 'flex', alignItems: 'center', padding: '6px 14px', borderBottom: '1px solid #f5f3ef', background: '#FFF8F6' },
        rowFocus: { boxShadow: 'inset 3px 0 0 #1B2A4A' },
        tNameFocus: { fontWeight: 700, color: '#1a1613' },
        note: { fontSize: 11, color: '#948b7f', marginTop: 4 },
        tName: { flex: 1, fontSize: 12, color: '#3a342c', minWidth: 0 },
        tVal: { fontSize: 13, fontWeight: 700, color: '#1a1613', marginRight: 4 },
        tValAbn: { fontSize: 13, fontWeight: 700, color: '#C53030', marginRight: 4 },
        tUnits: { fontSize: 10, color: '#948b7f', marginRight: 10 },
        tRef: { fontSize: 10, color: '#b0a89c', fontFamily: 'monospace' },
        tFlag: { fontSize: 9, fontWeight: 700, color: '#C53030', marginLeft: 6 },
        footer: { padding: '12px 24px', borderTop: '1px solid #e4ded5', textAlign: 'center' },
        fullBtn: { background: 'none', border: '1px solid #e4ded5', borderRadius: 8, padding: '8px 20px', fontSize: 12, fontWeight: 600, color: '#e8653a', cursor: 'pointer' },
    };

    return (
        <div style={s.overlay} onClick={onClose} data-testid="n3-lab-modal">
            <div style={s.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Lab results ${labGroup?.dateStr || ''}`.trim()}>
                <div style={s.header}>
                    <div>
                        <div style={s.title}>Lab Results</div>
                        <div style={s.date}>{labGroup?.dateStr || '—'}</div>
                        {labGroup?.partial ? <div style={s.note}>Key values from CareIQ · Open Full Labs for ranges</div> : null}
                    </div>
                    <div style={s.stats}>
                        <span style={s.pill}>{totalCount} tests</span>
                        {abnormalCount > 0 && <span style={s.pillAbn}>{abnormalCount} abnormal</span>}
                        <button ref={closeRef} style={s.closeBtn} onClick={onClose} aria-label="Close lab results">✕</button>
                    </div>
                </div>
                <div style={s.body}>
                    {panels.length > 0 ? panels.map((panel, pi) => {
                        const abnTests = panel.tests.filter(t => t.abnormal);
                        return (
                            <div key={pi} style={panel.hasAbnormal ? s.cardAbn : s.card}>
                                <div style={s.cardHead}>
                                    <span style={s.cardTitle}>{panel.name}</span>
                                    {abnTests.length > 0 && (
                                        <span style={s.cardBadge}>{abnTests.length} abnormal</span>
                                    )}
                                </div>
                                <div style={s.cardBody}>
                                    {panel.tests.map((t, ti) => (
                                        <div key={ti} style={{ ...(t.abnormal ? s.rowAbn : s.row), ...(t.focused ? s.rowFocus : null) }} data-focus={t.focused ? "1" : undefined}>
                                            <span style={t.focused ? { ...s.tName, ...s.tNameFocus } : s.tName}>{t.name}</span>
                                            <span style={t.abnormal ? s.tValAbn : s.tVal}>{t.value}</span>
                                            {t.units && <span style={s.tUnits}>{t.units}</span>}
                                            {t.refrange && <span style={s.tRef}>{t.refrange}</span>}
                                            {t.abnormal && <span style={s.tFlag}>⚠</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }) : (
                        <div style={{ textAlign: 'center', padding: 40, color: '#948b7f', fontSize: 13, fontStyle: 'italic' }}>
                            No lab results available for this date
                        </div>
                    )}
                </div>
                {onSwitchToLabs ? (
                    <div style={s.footer}>
                        <button style={s.fullBtn} onClick={onSwitchToLabs}>Open Full Labs →</button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

/* ═══ Generic Detail Modal (for CareIQ items) ═══ */
export const DetailModal = ({ item, onClose }) => {
    const title = item.label || item.text || item.item || item.recommendation || item.order || item.risk || item.priority_area || item.area || "Detail";
    const body = item.value || item.description || item.recommendation || item.item || item.order || item.risk || "";
    const ref = item.guideline_reference;

    return (
        <div className="ciqt-modal-bg" onClick={onClose}>
            <div className="ciqt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ciqt-modal-head">
                    <span className="ciqt-modal-t">{title}</span>
                    <button className="ciqt-modal-x" onClick={onClose}>✕</button>
                </div>
                <div className="ciqt-modal-body">
                    {body && <p>{body}</p>}
                    {item.status && <p><strong>Status:</strong> {item.status}</p>}
                    {item.type && <p><strong>Type:</strong> {item.type}</p>}
                    {ref && (
                        <div className="ciqt-guideline-box">
                            <h4>📎 Guideline Reference</h4>
                            {ref.source && <p><strong>Source:</strong> {ref.source}</p>}
                            {ref.link && ref.link !== "NOT_FOUND" && (
                                <p><a href={ref.link} target="_blank" rel="noreferrer">{ref.link}</a></p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export function isOutOfRange(value, refRange) {
    if (!value || !refRange) return false;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return false;
    const cleaned = refRange.replace(/\s/g, "");
    if (cleaned.includes("-")) {
        const [min, max] = cleaned.split("-").map(Number);
        if (isNaN(min) || isNaN(max)) return false;
        return numericValue < min || numericValue > max;
    }
    if (cleaned.startsWith("<=")) return numericValue > Number(cleaned.replace("<=", ""));
    if (cleaned.startsWith(">=")) return numericValue < Number(cleaned.replace(">=", ""));
    return false;
}

